import { useEffect, useRef, useState, type MouseEvent, type KeyboardEvent } from 'react'

import { api, ApiError } from '~/api/client'

import {
  centred,
  contain,
  coverScale,
  MAX_ZOOM,
  sourceRect,
  zoomTo,
  type Frame,
  type Placed,
} from './crop'

import s from './Avatar.module.css'

/**
 * Somebody's face — square, on the profile and the masthead. The round ones on a gig are
 * `components/Faces`, built from this file's pieces: `lettersOf`, `usePhoto`, `avatarUrl`
 * and the grade in the stylesheet. Each is exported so there is one rule for a face, not two.
 *
 * With no photo it falls back to initials on the same plate the cards use for a gig with no
 * artwork. Deliberately not a generated identicon: a coloured pattern derived from a
 * username is a machine pretending to know something about a person, and it reads as noise
 * next to a page of real photographs.
 */

/**
 * Fired when somebody replaces their own photograph, so the ones on screen catch up.
 *
 * `/avatar/<id>` is the same address before and after, and an `<img>` that is already
 * showing does not ask again for a `src` that has not changed — so a new picture appeared
 * only on the next page load. The server sends `no-store`, which is why a *reload* was
 * enough and why nobody else ever saw a stale one: the only copy that goes on existing is
 * the one already painted in this tab.
 *
 * The same shape as `waitingChanged` next door, and for the same reason: the picker is on
 * the profile, the other face is on the masthead above every route, and there is no
 * component that owns both.
 */
const CHANGED = 'lg:avatar'

/**
 * Say whose photograph has been replaced.
 *
 * Whose, and not merely that one was. It used to be a bare event every face on the page
 * answered, which was harmless while the version only ever changed a query string — but it
 * is now also what tells a face there is a photograph at all, and a face acting on somebody
 * else's news would go asking for a picture that does not exist.
 */
export function avatarChanged(accountId: number): void {
  window.dispatchEvent(new CustomEvent(CHANGED, { detail: accountId }))
}

/** How many times *this* account's photograph has been replaced in this tab. */
export function useAvatarVersion(accountId: number): number {
  const [version, setVersion] = useState(0)
  useEffect(() => {
    const bump = (event: Event) => {
      if ((event as CustomEvent<number>).detail !== accountId) return
      setVersion(n => n + 1)
    }
    window.addEventListener(CHANGED, bump)
    return () => window.removeEventListener(CHANGED, bump)
  }, [accountId])
  return version
}
/**
 * Up to two initials, taken from the words that start with one.
 *
 * A plain first-character-of-the-first-two-words turned `Claude (test)` into `C(`, and does
 * the same to `Sam (they/them)` — any display name whose second word is punctuated. A
 * bracket is not an initial, so the word is skipped rather than counted.
 *
 * `\p{L}` rather than `A-Z`, because a name is not necessarily Latin and an initial taken
 * from a name this cannot read is better than a full stop.
 */
export function lettersOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(word => /^[\p{L}\p{N}]/u.test(word))
    .slice(0, 2)
    .map(word => word[0] ?? '')
    .join('')
    .toUpperCase()
}

/**
 * Where somebody's photograph is fetched from.
 *
 * The query is what makes it a different address, which is what makes the browser ask
 * again after an upload. Absent until something changes, so the ordinary case is a clean
 * URL — and the one the document's image list can reuse across every face on a page that
 * asks for the same account.
 */
export function avatarUrl(accountId: number, version: number): string {
  return version ? `/avatar/${accountId}?v=${version}` : `/avatar/${accountId}`
}

/**
 * Photograph or initials, decided once for every face on the site.
 *
 * `src` is the address to draw, or null when the plate with the initials is the answer;
 * `onError` goes on the `<img>` so a picture that fails to load falls back to the plate.
 * The square face on the profile and the round ones on a card both ask this, so a change
 * to the address or to when the plate is shown reaches every face at once.
 *
 * `has` is the payload's answer, and on a *first* upload it is a render behind: the
 * profile reloads its own person and the masthead does not, so the face above every route
 * was still being told there is no photograph. The version knows better — it only moves
 * for an upload that has already been accepted, and only for this account.
 */
export function usePhoto(
  accountId: number,
  has: boolean,
): { src: string | null; onError: () => void } {
  // The version this face gave up at, rather than a flag. A photograph that failed to load
  // once must not condemn the one that replaces it — and it did, because `broken` outlived
  // the change: a first upload arrives at `/avatar/<id>` a moment after a 404 from the same
  // address, so the plate stayed on the initials it had just fallen back to.
  const [brokeAt, setBrokeAt] = useState<number | null>(null)
  const version = useAvatarVersion(accountId)
  const broken = brokeAt === version
  return {
    src: (!has && version === 0) || broken ? null : avatarUrl(accountId, version),
    onError: () => setBrokeAt(version),
  }
}

export function Avatar({
  accountId,
  name,
  has,
  size = 'normal',
}: {
  accountId: number
  name: string
  has: boolean
  size?: 'normal' | 'small'
}) {
  const { src, onError } = usePhoto(accountId, has)
  const initials = lettersOf(name)

  if (src === null) {
    return (
      <span className={`${s.face} ${s[size]} ${s.none}`} aria-hidden="true">
        <span className={s.mono}>{initials || '·'}</span>
      </span>
    )
  }
  return (
    <span className={`${s.face} ${s[size]}`}>
      <img className={s.photo} src={src} alt="" onError={onError} />
    </span>
  )
}

/**
 * Choosing one.
 *
 * The resize happens here rather than on the server, and that is a decision rather than a
 * convenience: the alternative is an image library on a backend that has stayed
 * stdlib-only on purpose. A canvas does it in four lines and the bytes that arrive are
 * already the size they will be served at.
 *
 * One encoding was assumed to be small enough and is not always. A 400px square at quality
 * 0.82 is usually 30 to 50KB, and a 2.7MB photograph still came back over the server's cap
 * — `toDataURL` is free to ignore the quality argument, and a browser that falls back to
 * PNG turns the same square into several hundred kilobytes. Assuming was the mistake: the
 * only thing that guarantees the body fits is measuring it.
 *
 * So it encodes, measures, and tries again smaller until it fits — quality first, because
 * that costs the least, then the square itself. What arrives is bounded whatever the
 * browser decided to do with the arguments.
 */
const SIDE = 400
const QUALITY = 0.82

//: How large the picture may be, in bytes, once decoded.
//:
//: `people.MAX_AVATAR`, and it has to be that number rather than one near it: the server
//: measures the decoded bytes and so does this, so a picture that passes here passes there.
//: Measuring the data URL's own length instead would have compared a string against a
//: number a third smaller than itself.
//:
//: This was the cap the whole time and not the body limit. It was 48KB, in the middle of
//: the range a 400px square actually encodes to — so which photographs were refused came
//: down to how busy their backgrounds were.
const LIMIT = 256 * 1024

/**
 * How many bytes a data URL decodes to, without decoding it.
 *
 * Base64 is four characters per three bytes, less the padding. Exact rather than
 * approximate, because it is compared against the number the server will apply.
 */
export function decodedSize(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return Math.floor((base64.length * 3) / 4) - padding
}

//: What to try, in order, before giving up. Quality first: a 400px square at 0.4 is still a
//: face, where a 200px one is a thumbnail. Each is an encode, and an encode is milliseconds.
const ATTEMPTS: readonly [number, number][] = [
  [SIDE, QUALITY],
  [SIDE, 0.6],
  [SIDE, 0.4],
  [SIDE / 2, 0.6],
  [SIDE / 2, 0.4],
]

/**
 * The first encoding that fits, or the smallest one if none of them do.
 *
 * Separated from the canvas so it can be checked without one: jsdom has no `toDataURL`, and
 * the part worth checking is the choosing rather than the drawing.
 *
 * Returning the smallest rather than throwing, because a picture that is still too big
 * after every attempt is a picture the server should refuse and say so — this has no
 * business inventing that message a second time.
 */
export function bestFit(
  encode: (side: number, quality: number) => string,
  limit: number = LIMIT,
): string {
  let smallest = ""
  for (const [side, quality] of ATTEMPTS) {
    const out = encode(side, quality)
    if (decodedSize(out) <= limit) return out
    if (!smallest || decodedSize(out) < decodedSize(smallest)) smallest = out
  }
  return smallest
}

/**
 * What an iPhone camera produces by default, and what a browser may not be able to open.
 *
 * The type is checked as well as the name because a picker does not always give both: iOS
 * hands over `image/heic`, some desktops hand over an empty type and a `.HEIC` name.
 */
export function isHeic(file: File): boolean {
  return /heic|heif/i.test(file.type) || /\.hei[cf]$/i.test(file.name)
}

/**
 * The photograph, decoded, so it can be shown and cropped before anything is sent.
 *
 * Was `shrink(file)`: read, crop from the middle, encode, upload, in one call nobody could
 * step into. A crop somebody chooses needs the middle of that pulled apart — the picture on
 * screen while they drag it, and the encode only once they are done.
 *
 * This is also the whole of the site's image decoding. The server sniffs the magic bytes to
 * check what arrived is a JPEG or a PNG, stores it and serves it back — it never decodes
 * anything, which is the point of a backend that has stayed stdlib-only. So whether a HEIC
 * can be uploaded is a question about *this browser*, not about the server: where it can be
 * read, the canvas re-encodes it to JPEG like any other picture and what arrives is a JPEG.
 * Safari reads them; most desktop browsers do not.
 *
 * Which makes the failure worth naming. "That file is not a picture" is wrong about a
 * photograph somebody just took, and leaves them with nothing to try.
 */
export function readPicture(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('that file could not be read'))
    reader.onload = () => {
      const image = new Image()
      image.onerror = () =>
        reject(
          new Error(
            isHeic(file)
              ? 'this browser cannot open HEIC photos — try it on your phone, or export the ' +
                'picture as JPEG first'
              : 'that file is not a picture',
          ),
        )
      image.onload = () => resolve(image)
      image.src = String(reader.result)
    }
    reader.readAsDataURL(file)
  })
}

/**
 * The square somebody chose, as a data URL bounded by what the server takes.
 *
 * `sourceRect` answers in the photograph's own pixels, so what comes out does not depend on
 * how big the preview happened to be on screen — which matters, because the preview is
 * sized by CSS and the exported square never is.
 */
export function square(image: HTMLImageElement, place: Placed, viewport: number): string {
  const canvas = document.createElement('canvas')
  const brush = canvas.getContext('2d')
  if (!brush) throw new Error('this browser cannot resize pictures')
  const { sx, sy, sw, sh } = sourceRect(place, viewport)
  return bestFit((out, quality) => {
    // Redrawn per attempt: changing `width` clears the canvas, so the picture has to go back
    // on at the new size before anything is read off it.
    canvas.width = canvas.height = out
    brush.drawImage(image, sx, sy, sw, sh, 0, 0, out, out)
    return canvas.toDataURL('image/jpeg', quality)
  })
}

//: How big the square you drag the picture around in is, in CSS pixels.
//:
//: A number rather than a measurement of the element, because `sourceRect` divides
//: everything by the same scale — so the answer is in the photograph's own pixels either
//: way, and reading the DOM for it would be a layout pass per drag event to learn a constant.
//: The stylesheet holds the same number and the two have to agree.
const PREVIEW = 260

/**
 * Choosing the square of a photograph that becomes somebody's face.
 *
 * Three states, and the middle one is the point: pick a file, put it where you want it, and
 * only then does anything get encoded or sent. It used to crop the middle and upload in one
 * gesture, which is right about where a face usually is and wrong about every photograph
 * where it is not.
 *
 * The arithmetic is all in `crop.ts` and was written before this existed — every route into
 * a new position goes through `contain`, so a drag, the slider and the arrow keys cannot
 * disagree about what is legal.
 */
export function AvatarPicker({ accountId, onDone }: { accountId: number; onDone: () => void }) {
  const input = useRef<HTMLInputElement>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [picture, setPicture] = useState<HTMLImageElement | null>(null)
  const [place, setPlace] = useState<Placed | null>(null)
  // The pointer's last position and whether it is down. A ref rather than state: a drag
  // fires on every pixel of movement and none of them is worth a render of its own — the
  // render comes from `setPlace`, which is the thing that actually moved.
  const drag = useRef<{ x: number; y: number } | null>(null)
  const dialog = useRef<HTMLDialogElement>(null)
  const stage = useRef<HTMLSpanElement>(null)
  const opener = useRef<HTMLButtonElement>(null)

  /*
   * A modal, and the two halves of that word the markup cannot supply: focus goes in when it
   * opens and back to whatever opened it when it closes. `showModal` is guarded because
   * jsdom does not implement `<dialog>` — the fallback sets the attribute the browser would
   * have set, which is enough for the tests and for the CSS to match.
   *
   * The same shape as `ArtistSheet` next door. Inline, this sat in the profile's own flex row
   * and threw the slider and the buttons to the far side of the page, a foot from the picture
   * they act on.
   *
   * Held by reference, and the button it points at is drawn whether or not the window is up.
   * This used to read `document.activeElement` when the picture arrived and focus that on the
   * way out — and it focused nothing, every time, because the component rendered *either* the
   * window or the button and never both. The element it had captured was detached by the time
   * it was called, so Escape, Cancel, the backdrop and Save all left focus on `<body>`. It is
   * also why the row underneath went from three buttons to two while the window was open.
   */
  useEffect(() => {
    if (!picture) return undefined
    return () => opener.current?.focus()
  }, [picture])

  /*
   * Opened after every render, not only the first, and it has to be.
   *
   * React manages `open` on a `<dialog>` and this one never passes it — so on the next
   * render after `showModal`, React sets it back to closed. Pressing Save is a render, which
   * made the window vanish while the upload it started was still going.
   *
   * `showModal` rather than the prop because only that draws the backdrop and makes it
   * modal; the prop opens an inline dialog with the page still live behind it. Guarded
   * because jsdom does not implement it, and the attribute is what the CSS and the tests
   * both read.
   */
  useEffect(() => {
    const element = dialog.current
    if (!element || !picture || element.open) return
    if (typeof element.showModal === 'function') element.showModal()
    else element.setAttribute('open', '')
  })

  const shut = () => {
    setPicture(null)
    setPlace(null)
    setFailed(null)
  }

  const frame: Frame = { width: picture?.width ?? 0, height: picture?.height ?? 0 }
  const floor = picture ? coverScale(frame, PREVIEW) : 1

  const chosen = async (file: File | undefined) => {
    if (!file) return
    setFailed(null)
    try {
      const image = await readPicture(file)
      setPicture(image)
      setPlace(centred({ width: image.width, height: image.height }, PREVIEW))
    } catch (error: unknown) {
      setFailed(error instanceof Error ? error.message : String(error))
    }
  }

  /*
   * The wheel, and a trackpad pinch, which arrives as one with `ctrlKey` set.
   *
   * A native listener rather than `onWheel`, because React registers its wheel handler as
   * passive and `preventDefault` inside one does nothing but warn — so the page would scroll
   * away underneath the square while somebody zoomed it.
   *
   * Exponential, so a notch of the wheel changes the picture by the same proportion at every
   * scale: linear steps crawl when zoomed out and leap when zoomed in, and cannot be trusted
   * not to cross zero. `zoomTo` clamps whatever comes out, so the limits are still one rule
   * in one place.
   */
  useEffect(() => {
    const element = stage.current
    if (!element || !picture) return undefined
    const wheel = (e: WheelEvent) => {
      e.preventDefault()
      // Lines rather than pixels on some browsers, which are about sixteen pixels each.
      const by = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY
      setPlace(was =>
        was ? zoomTo(frame, PREVIEW, was, was.scale * Math.exp(-by * 0.0015)) : was,
      )
    }
    element.addEventListener('wheel', wheel, { passive: false })
    return () => element.removeEventListener('wheel', wheel)
  }, [picture, frame.width, frame.height])

  const move = (by: { x: number; y: number }) =>
    setPlace(was => (was && picture ? contain(frame, PREVIEW, { ...was, x: was.x + by.x, y: was.y + by.y }) : was))

  const save = async () => {
    if (!picture || !place) return
    setBusy(true)
    setFailed(null)
    try {
      await api.me({ avatar: square(picture, place, PREVIEW) })
      // Before `onDone`, so the faces already on screen are asking for the new one by the
      // time the page reloads its payload around them.
      avatarChanged(accountId)
      shut()
      onDone()
    } catch (error: unknown) {
      // What the server said, not where it said it. The whole `ApiError.message` put
      // `/me answered 503: … ERR_NGROK_3004` under the button — a proxy the reader has
      // never heard of, and nothing at all about their picture.
      setFailed(
        error instanceof ApiError
          ? error.detail
          : error instanceof Error
            ? error.message
            : String(error),
      )
    } finally {
      setBusy(false)
    }
  }

  const window_ =
    picture && place ? (
      /*
       * Escape and the backdrop both close it, handled here rather than left to the
       * browser's own close-watcher: a `<dialog>` that closes itself would leave this
       * component still thinking it is open.
       */
      <dialog
        ref={dialog}
        className={s.crop}
        aria-modal="true"
        aria-labelledby="crop-title"
        onClick={(e: MouseEvent<HTMLDialogElement>) => {
          if (e.target === dialog.current) shut()
        }}
        onKeyDown={(e: KeyboardEvent<HTMLDialogElement>) => {
          if (e.key !== 'Escape') return
          e.preventDefault()
          shut()
        }}
        onCancel={e => {
          e.preventDefault()
          shut()
        }}
      >
        {/* Named, like any window. `aria-labelledby` rather than a second copy of the words
            in an `aria-label`, so what a screen reader announces is what is on screen. */}
        <h2 className={s.title} id="crop-title">
          Choose the square
        </h2>
        {/*
          * Draggable, and reachable without a pointer. Arrow keys move it and the slider
          * zooms, because a control you can only operate by dragging is one somebody on a
          * keyboard cannot operate at all.
          */}
        <span
          ref={stage}
          className={s.stage}
          role="group"
          aria-label="Move the picture to choose the square"
          tabIndex={0}
          onPointerDown={e => {
            drag.current = { x: e.clientX, y: e.clientY }
            // Optional, and not only for the tests. Pointer capture is what keeps the drag
            // alive when the pointer leaves the square, which is a nicety; calling it
            // unguarded threw in every environment that does not implement it and took the
            // whole gesture down with it.
            e.currentTarget.setPointerCapture?.(e.pointerId)
          }}
          onPointerMove={e => {
            if (!drag.current) return
            move({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y })
            drag.current = { x: e.clientX, y: e.clientY }
          }}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
          onKeyDown={e => {
            const step = e.shiftKey ? 20 : 4
            const by: Record<string, { x: number; y: number }> = {
              ArrowLeft: { x: -step, y: 0 },
              ArrowRight: { x: step, y: 0 },
              ArrowUp: { x: 0, y: -step },
              ArrowDown: { x: 0, y: step },
            }
            const going = by[e.key]
            if (!going) return
            e.preventDefault()
            move(going)
          }}
        >
          <img
            className={s.placed}
            src={picture.src}
            alt=""
            draggable={false}
            style={{
              width: frame.width * place.scale,
              height: frame.height * place.scale,
              left: place.x,
              top: place.y,
            }}
          />
        </span>

        <input
          className={s.zoom}
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={place.scale / floor}
          aria-label="Zoom"
          disabled={busy}
          onChange={e => setPlace(was => (was ? zoomTo(frame, PREVIEW, was, floor * Number(e.target.value)) : was))}
        />

        <span className={s.row}>
          <button type="button" className={s.pick} disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {/* Not disabled while saving. Escape closes a modal whatever is going on — the
              browser sees to that — so a Cancel that refused would be the same gesture
              answering two ways. An upload already in flight finishes either way, and says
              so: the faces on the page ask again when it lands. */}
          <button type="button" className={s.quiet} onClick={shut}>
            Cancel
          </button>
        </span>
        {failed ? <span className={s.trouble}>{failed}</span> : null}
      </dialog>
    ) : null

  return (
    <>
      <span className={s.picker}>
        <button
          ref={opener}
          type="button"
          className={s.pick}
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          Change photo
        </button>
        <input
          ref={input}
          type="file"
          /* HEIC is here because it is what an iPhone camera produces by default, so it is
           the first thing most people reach for — and the picker was hiding it. Whether it
           opens is the browser's business: Safari reads them, the canvas re-encodes to JPEG
           like anything else, and the server sees a JPEG either way. Where it cannot be
           read, `readPicture` says which format failed rather than calling a photograph
           "not a picture".

           Named rather than `image/*`, which would also offer SVG — a file the canvas will
           take and that has no business being somebody's face. */
        accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
          hidden
          onChange={e => void chosen(e.target.files?.[0])}
        />
        {/* Only when there is no window to say it in, or the same sentence is on screen
            twice — once under the picture it is about and once behind the backdrop. */}
        {failed && !window_ ? <span className={s.trouble}>{failed}</span> : null}
      </span>
      {window_}
    </>
  )
}
